// src/i18n/index.ts
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

// Supported languages
export const SUPPORTED_LANGUAGES = ['ja', 'en', 'zh-CN', 'ko'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Language display names
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  'ja': '日本語',
  'en': 'English',
  'zh-CN': '简体中文',
  'ko': '한국어'
};

// Namespace list
const NAMESPACES = ['common', 'top', 'footer', 'create', 'character', 'adopt', 'settings', 'auth', 'vtuberInvite'] as const;

// Track loaded languages
const loadedLanguages = new Set<string>();

// Detect browser language
const detectBrowserLanguage = (): SupportedLanguage => {
  const browserLang = navigator.language;
  const shortLang = browserLang.split('-')[0];

  if (shortLang === 'zh') return 'zh-CN';
  if (['ja', 'en', 'ko'].includes(shortLang)) return shortLang as SupportedLanguage;
  return 'ja'; // Default to Japanese for ORIFOLI
};

// Get saved or detected language
const getSavedLanguage = (): SupportedLanguage => {
  const saved = localStorage.getItem('orifoli_language');
  if (saved && SUPPORTED_LANGUAGES.includes(saved as SupportedLanguage)) {
    return saved as SupportedLanguage;
  }
  return detectBrowserLanguage();
};

// Dynamic import function for language resources
const loadLanguageResources = async (lng: string): Promise<void> => {
  if (loadedLanguages.has(lng)) return;

  const resources: Record<string, unknown> = {};

  // Use Vite's glob import for dynamic loading
  const modules = import.meta.glob('./locales/**/*.json');

  const loadPromises = NAMESPACES.map(async (ns) => {
    const path = `./locales/${lng}/${ns}.json`;
    const loader = modules[path];
    if (loader) {
      try {
        const module = await loader() as { default: unknown };
        resources[ns] = module.default;
      } catch (e) {
        console.warn(`Failed to load ${path}:`, e);
      }
    }
  });

  await Promise.all(loadPromises);

  // Add resources to i18next
  Object.entries(resources).forEach(([ns, data]) => {
    i18next.addResourceBundle(lng, ns, data, true, true);
  });

  loadedLanguages.add(lng);
};

// Initialize i18next with minimal config (no resources yet)
const initPromise = i18next
  .use(initReactI18next)
  .init({
    lng: getSavedLanguage(),
    fallbackLng: 'ja',
    interpolation: {
      escapeValue: false,
    },
    resources: {},
    react: {
      useSuspense: true,
    },
  });

// Load initial language only (defer fallback for faster startup)
const initialLanguage = getSavedLanguage();
const initializeLanguages = async () => {
  await initPromise;

  // Load current language first (critical path)
  await loadLanguageResources(initialLanguage);

  // Defer fallback language loading to after page render
  if (initialLanguage !== 'ja') {
    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
        .requestIdleCallback(() => {
          loadLanguageResources('ja');
        }, { timeout: 3000 });
    } else {
      setTimeout(() => {
        loadLanguageResources('ja');
      }, 1000);
    }
  }
};

// Start loading immediately
const languageReadyPromise = initializeLanguages();

// Export promise for components that need to wait
export const waitForLanguage = () => languageReadyPromise;

// Change language function with lazy loading
export const changeLanguage = async (lng: string): Promise<void> => {
  if (!SUPPORTED_LANGUAGES.includes(lng as SupportedLanguage)) {
    console.warn(`Unsupported language: ${lng}`);
    return;
  }

  // Load resources if not already loaded
  await loadLanguageResources(lng);

  // Change language
  await i18next.changeLanguage(lng);
  localStorage.setItem('orifoli_language', lng);
};

export default i18next;
